import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { FileText, ArrowRight, CheckCircle, CaretDown, CaretRight, Certificate, WarningCircle } from "@phosphor-icons/react";
import { portalApi } from "@/lib/portalApi";
import { formatApiError } from "@/lib/api";
import { BRAND } from "./portalBrand";

const money = (c, n) => `${c || ""} ${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`.trim();

// Subtotal line items by their own currency — never sum USD and INR together.
const perCurrencyTotals = (items) => {
  const m = {};
  (items || []).forEach((it) => { const c = it.currency || "INR"; m[c] = (m[c] || 0) + Number(it.amount || 0); });
  return Object.entries(m); // e.g. [["USD", 2000], ["INR", 5000]]
};
const totalsText = (q) => {
  const entries = perCurrencyTotals(q.items);
  if (entries.length === 0) return money(q.currency, q.total_amount);
  return entries.map(([c, a]) => money(c, a)).join("  ·  ");
};

const statusChip = (s) => {
  const x = (s || "").toLowerCase();
  if (x.includes("accept")) return "text-emerald-700 border-emerald-200 bg-emerald-50";
  return "text-blue-700 border-blue-200 bg-blue-50";
};

export default function PortalQuotes() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [busy, setBusy] = useState("");

  const load = () => portalApi.get("/portal/quotes").then(({ data }) => setRows(data || [])).catch((e) => toast.error(formatApiError(e))).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const book = async (q) => {
    const again = q.status === "Accepted";
    const label = again
      ? `Place a new booking against ${q.rate_agreement_no || q.quote_number}?`
      : `Accept quote ${q.quote_number} and place a booking?`;
    if (!window.confirm(label)) return;
    setBusy(q.id);
    try {
      const { data } = await portalApi.post(`/portal/quotes/${q.id}/book`);
      toast.success(`Booking ${data.booking_no} placed from ${q.rate_agreement_no || q.quote_number}.`);
      navigate("/portal/bookings");
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };

  // Backend computes rate_expired against the RA window (falls back to the quote's validity).
  const expired = (q) => q.rate_expired;
  const validTill = (q) => (q.ra_valid_until || q.valid_until || "").slice(0, 10);

  return (
    <div>
      <div className="mb-5">
        <h1 className="text-2xl font-bold text-slate-900">Your Quotes</h1>
        <p className="text-slate-500 text-sm mt-1">Rates {BRAND.name} has prepared for you. Accept one to place a booking instantly.</p>
      </div>

      {loading ? (
        <div className="text-slate-400 text-sm">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl py-14 text-center text-slate-500">
          No quotes yet. Use <span className="text-blue-700 font-medium">Request a Quote</span> to ask for a rate.
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((q) => {
            const isOpen = openId === q.id;
            return (
              <div key={q.id} className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                <button onClick={() => setOpenId(isOpen ? null : q.id)} className="w-full text-left px-4 py-3 flex items-center justify-between gap-3 hover:bg-slate-50">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <FileText size={15} className="text-blue-600" />
                      <span className="font-mono font-semibold text-slate-800 text-sm">{q.quote_number}</span>
                      <span className="text-[10px] font-semibold uppercase tracking-wider rounded-full px-2 py-0.5 border border-slate-200 bg-slate-50 text-slate-600">{q.mode}</span>
                      <span className="text-sm text-slate-700">{q.pol_name || "—"} <ArrowRight size={12} className="inline -mt-0.5 text-slate-400" /> {q.pod_name || "—"}</span>
                    </div>
                    <div className="text-[11px] text-slate-400 mt-1 flex items-center gap-2 flex-wrap">
                      <span>{q.quote_date ? `Dated ${q.quote_date.slice(0, 10)}` : ""}{validTill(q) ? ` · valid till ${validTill(q)}${expired(q) ? " (expired)" : ""}` : ""}</span>
                      {q.rate_agreement_no && (
                        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 border text-[10px] font-semibold ${expired(q) ? "border-slate-200 bg-slate-50 text-slate-500" : "border-emerald-200 bg-emerald-50 text-emerald-700"}`} title="Quote your Rate Agreement number when booking this lane again">
                          <Certificate size={11} weight="bold" /> {q.rate_agreement_no}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="text-sm font-bold text-slate-900 tabular-nums whitespace-nowrap">{totalsText(q)}</span>
                    <span className={`text-[10px] font-semibold uppercase tracking-wider rounded-full px-2 py-0.5 border ${statusChip(q.status)}`}>{q.status}</span>
                    {isOpen ? <CaretDown size={14} className="text-slate-400" /> : <CaretRight size={14} className="text-slate-400" />}
                  </div>
                </button>

                {isOpen && (
                  <div className="border-t border-slate-100 px-4 py-3">
                    <table className="w-full text-sm">
                      <thead className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 border-b border-slate-200">
                        <tr><th className="text-left py-2">Charge</th><th className="text-right py-2">Qty</th><th className="text-left py-2 pl-3">Unit</th><th className="text-right py-2">Rate</th><th className="text-right py-2">Amount</th></tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {(q.items || []).map((it, i) => (
                          <tr key={i}>
                            <td className="py-2 text-slate-700">{it.description || it.charge_code || "—"}</td>
                            <td className="py-2 text-right text-slate-600">{it.quantity}</td>
                            <td className="py-2 pl-3 text-slate-500">{it.uom || "—"}</td>
                            <td className="py-2 text-right tabular-nums text-slate-600">{money(it.currency, it.rate)}</td>
                            <td className="py-2 text-right tabular-nums text-slate-800">{money(it.currency, it.amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        {perCurrencyTotals(q.items).map(([c, a], idx) => (
                          <tr key={c} className={idx === 0 ? "border-t-2 border-slate-200" : ""}>
                            <td colSpan={4} className="py-2 text-right font-semibold text-slate-700">Total ({c})</td>
                            <td className="py-2 text-right font-bold text-slate-900 whitespace-nowrap">{money(c, a)}</td>
                          </tr>
                        ))}
                      </tfoot>
                    </table>
                    {q.rate_agreement_no && (
                      <div className={`mt-3 rounded-md border px-3 py-2 text-xs flex items-start gap-2 ${expired(q) ? "border-slate-200 bg-slate-50 text-slate-500" : "border-emerald-200 bg-emerald-50 text-emerald-800"}`}>
                        <Certificate size={14} weight="bold" className="mt-0.5 shrink-0" />
                        <span>
                          Rate Agreement <span className="font-mono font-semibold">{q.rate_agreement_no}</span>
                          {validTill(q) ? ` · valid till ${validTill(q)}` : ""}.
                          {expired(q) ? " This rate has expired — request a fresh quote." : " Book this lane again anytime while it's valid."}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-end mt-3">
                      {expired(q) ? (
                        <button disabled className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-slate-200 text-slate-500 text-sm font-semibold cursor-not-allowed">
                          <WarningCircle size={16} weight="bold" /> Rate expired
                        </button>
                      ) : (
                        <button onClick={() => book(q)} disabled={busy === q.id} className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-blue-700 hover:bg-blue-800 text-white text-sm font-semibold disabled:opacity-50">
                          <CheckCircle size={16} weight="bold" /> {busy === q.id ? "Placing…" : q.status === "Accepted" ? "Book again" : "Accept & Book"}
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
